import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, FileText, ArrowRightCircle, Search, Edit3, Download, Printer, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatMoney, docCurrency } from "@/lib/currency";

const STATUS_TONE = {
  draft:     "bg-slate-100 text-slate-700",
  sent:      "bg-blue-50 text-blue-700",
  approved:  "bg-emerald-50 text-emerald-700",
  rejected:  "bg-red-50 text-red-700",
  converted: "bg-purple-50 text-purple-700",
};

export default function QuotationsPage() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  // ---- Phase 27: PDF / Print / Email actions --------------------------------
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", cc_email: "", subject: "", message: "" });
  const [emailTarget, setEmailTarget] = useState(null);

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quotation-${q.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${q.code}.pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF download failed");
    }
  };

  const printQuotation = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/render-html`);
      const w = window.open("", "_blank");
      if (!w) return toast.error("Popup blocked — please allow popups for this site");
      w.document.open();
      w.document.write(res.data);
      w.document.close();
      // wait for images then print
      setTimeout(() => { w.focus(); w.print(); }, 400);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Print failed");
    }
  };

  const openEmailDialog = (q) => {
    setEmailTarget(q);
    const totals = { grand_total: q.totals?.grand_total || 0 };
    const validity = q.validity_date ? new Date(q.validity_date).toLocaleDateString() : "—";
    const defaultBody = (
      `Dear ${q.customer_name},\n\n`
      + `Please find attached our quotation ${q.code} dated ${q.date ? new Date(q.date).toLocaleDateString() : ""} for your review.\n\n`
      + `Grand Total: ${formatMoney(Number(totals.grand_total), docCurrency(q))}\n`
      + `Valid till: ${validity}\n\n`
      + `Do let us know if you need any changes.\n\nRegards,\nCanares Automation`
    );
    setEmailForm({
      to_email: q.customer_email || "",
      cc_email: "",
      subject: `Quotation ${q.code} from Canares Automation`,
      message: defaultBody,
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailForm.to_email) return toast.error("Recipient email is required");
    setBusy(true);
    try {
      await api.post(`/quotations/${emailTarget.id}/email`, {
        to_email: emailForm.to_email,
        cc_email: emailForm.cc_email || null,
        subject: emailForm.subject,
        message: emailForm.message,
      });
      toast.success(`Quotation ${emailTarget.code} emailed to ${emailForm.to_email}`);
      setEmailOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed — check RESEND_API_KEY in Settings");
    } finally { setBusy(false); }
  };

  const load = async () => {
    const [q, i] = await Promise.all([
      api.get("/quotations"),
      api.get("/items"),
    ]);
    setQuotations(q.data);
    setItems(i.data);
    if (selected) setSelected(q.data.find((x) => x.id === selected.id) || null);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => navigate("/raise-quotation");
  const openEdit = (q) => navigate(`/raise-quotation/${q.id}`);

  const remove = async (q) => {
    if (!window.confirm(`Delete ${q.code}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success(`${q.code} deleted`);
      if (selected?.id === q.id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const convertToTO = async (q) => {
    try {
      const { data } = await api.get(`/quotations/${q.id}/convert-preview`);
      navigate("/raise-transfer-order", { state: { prefill: data.prefill } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cannot convert");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return quotations.filter(
      (x) => !q || x.code.toLowerCase().includes(q) || (x.customer_name || "").toLowerCase().includes(q),
    );
  }, [quotations, search]);

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Quotations
          </h1>
          <p className="text-sm text-slate-500 mt-1">Draft → Send → Approve → Convert to Transfer Order (FG items only).</p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-quotation-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Quotation
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Input placeholder="Search code or customer…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 border-0 shadow-none focus-visible:ring-0 px-0" data-testid="quotation-search" />
            <span className="text-xs text-slate-500 mono">{filtered.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {filtered.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500">No quotations yet.</div>
            )}
            {filtered.map((q) => (
              <div key={q.id} className={`px-4 py-3 hover:bg-slate-50 transition ${selected?.id === q.id ? "bg-blue-50/60" : ""}`} data-testid={`quo-row-${q.code}`}>
                <button
                  onClick={() => setSelected(q)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{q.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[q.status] || "bg-slate-100"}`}>{q.status}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{q.customer_name}</div>
                  <div className="mt-0.5 text-xs text-slate-500 flex items-center justify-between">
                    <span>{q.lines.length} line(s) · {formatMoney((q.totals?.grand_total || 0), docCurrency(q))}</span>
                    {q.transfer_order_code && (
                      <span className="text-purple-700 mono text-[10px]">→ {q.transfer_order_code}</span>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-1 mt-2">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => downloadPdf(q)} data-testid={`quo-pdf-row-${q.code}`} title="Download PDF">
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => printQuotation(q)} data-testid={`quo-print-row-${q.code}`} title="Print">
                    <Printer className="w-3.5 h-3.5 mr-1" /> Print
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openEmailDialog(q)} data-testid={`quo-email-row-${q.code}`} title="Send by Email">
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="quo-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <FileText className="w-10 h-10 text-slate-300" />
              Pick a quotation to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{selected.customer_name}</h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {selected.customer_contact && <>📞 {selected.customer_contact} · </>}
                    Date: {selected.date ? new Date(selected.date).toLocaleDateString() : "—"} ·
                    Valid till: {selected.validity_date ? new Date(selected.validity_date).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_TONE[selected.status]}`} data-testid="quo-status-pill">{selected.status}</span>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(selected)} data-testid="quo-pdf-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => printQuotation(selected)} data-testid="quo-print-btn">
                    <Printer className="w-4 h-4 mr-1.5" /> Print
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEmailDialog(selected)} data-testid="quo-email-btn">
                    <Mail className="w-4 h-4 mr-1.5" /> Send Email
                  </Button>
                  {selected.transfer_order_code ? (
                    <div className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-md px-3 py-1 mono" data-testid="linked-to-badge">
                      Linked TO → <button onClick={() => navigate("/sales-orders")} className="underline font-semibold">{selected.transfer_order_code}</button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => convertToTO(selected)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="convert-to-btn"
                      disabled={selected.status === "rejected"}
                    >
                      <ArrowRightCircle className="w-4 h-4 mr-1.5" /> Convert to Transfer Order
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(selected)} disabled={selected.status === "converted"} data-testid="quo-edit-btn">
                    <Edit3 className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(selected)} disabled={selected.status === "converted"}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="text-sm" style={{ minWidth: "1000px" }}>
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50 whitespace-nowrap">
                      <th className="py-2 px-3">Item</th>
                      <th className="py-2 px-3 text-right">Qty</th>
                      <th className="py-2 px-3">UOM</th>
                      <th className="py-2 px-3 text-right">Rate</th>
                      <th className="py-2 px-3">HSN</th>
                      <th className="py-2 px-3 text-right">GST %</th>
                      <th className="py-2 px-3 text-right">Disc %</th>
                      <th className="py-2 px-3">Delivery</th>
                      <th className="py-2 px-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lines.map((l, idx) => {
                      const gstPct = (Number(l.cgst_pct || 0) + Number(l.sgst_pct || 0) + Number(l.igst_pct || 0)) || Number(l.tax_pct || selected.gst_pct || 0);
                      const gross = Number(l.qty || 0) * Number(l.unit_price || 0);
                      const afterDisc = gross * (1 - Number(l.discount_pct || 0) / 100);
                      const amt = afterDisc * (1 + gstPct / 100);
                      return (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 px-3">
                            <div className="font-medium">{l.part_name || itemMap[l.item_id]?.name}</div>
                            <div className="text-[11px] text-slate-500 mono">{l.part_code || itemMap[l.item_id]?.code}</div>
                          </td>
                          <td className="py-2 px-3 text-right mono">{l.qty}</td>
                          <td className="py-2 px-3 text-xs">{l.uom}</td>
                          <td className="py-2 px-3 text-right mono">{formatMoney(Number(l.unit_price), docCurrency(selected))}</td>
                          <td className="py-2 px-3 text-xs mono">{l.hsn_code || "—"}</td>
                          <td className="py-2 px-3 text-right mono">{gstPct}%</td>
                          <td className="py-2 px-3 text-right mono">{Number(l.discount_pct || 0)}%</td>
                          <td className="py-2 px-3 text-xs">{l.delivery_date ? new Date(l.delivery_date).toLocaleDateString() : "—"}</td>
                          <td className="py-2 px-3 text-right mono font-semibold">{formatMoney(amt, docCurrency(selected), { maximumFractionDigits: 2 })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="w-full sm:w-80 space-y-1 bg-slate-50 border border-slate-100 rounded-md p-4 text-sm">
                  <Row label="Subtotal">{formatMoney((selected.totals?.subtotal || 0), docCurrency(selected))}</Row>
                  <Row label="Line Discount">− {formatMoney((selected.totals?.line_discount || 0), docCurrency(selected))}</Row>
                  <Row label={`Header Discount (${selected.header_discount || 0}%)`}>− {formatMoney((selected.totals?.header_discount_amt || 0), docCurrency(selected))}</Row>
                  <Row label="Total GST">+ {formatMoney((selected.totals?.tax || 0), docCurrency(selected))}</Row>
                  <Row label={selected.additional_charges_label || "Freight / Other"}>+ {formatMoney((selected.totals?.additional_charges || 0), docCurrency(selected))}</Row>
                  <div className="pt-2 border-t border-slate-200 flex items-center justify-between font-extrabold text-slate-900">
                    <span>Grand Total</span>
                    <span className="mono" data-testid="quo-grand-total">{formatMoney((selected.totals?.grand_total || 0), docCurrency(selected), { withCode: true })}</span>
                  </div>
                </div>
              </div>
              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic whitespace-pre-wrap">{selected.notes}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl" data-testid="quo-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Quotation {emailTarget?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To *</Label>
              <Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} data-testid="quo-email-to" />
            </div>
            <div>
              <Label>CC (optional)</Label>
              <Input type="email" value={emailForm.cc_email} onChange={(e) => setEmailForm({ ...emailForm, cc_email: e.target.value })} data-testid="quo-email-cc" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="quo-email-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={9} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} data-testid="quo-email-message" />
            </div>
            <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
              <strong>Attachment:</strong> Quotation-{emailTarget?.code}.pdf will be attached automatically.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="quo-email-send">
              <Mail className="w-4 h-4 mr-1.5" /> {busy ? "Sending…" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between text-slate-700">
      <span className="text-xs uppercase tracking-[0.1em] text-slate-500">{label}</span>
      <span className="mono">{children}</span>
    </div>
  );
}
